import React, { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SERVER_URL } from "../api";
import { getGroupById } from "../api/group";
import logoJohnsonAndJohnson from "../assets/logo-jandj.png";
import oncommit from "../assets/hemmexus.png";
import profileContainer from "../assets/results/circulo-perfil.svg";
import { setGame, setGroup } from "../redux/actions";
import { useLang } from "../translations/i18n";
import { ROUTES } from "../utils/constants";
import { clearTabletLocalSession, updateStats } from "../utils/helpers";

const THEME = {
  primary: "#B1000D",   // rojo corporativo
  soft:    "#FFF1F2",   // fondo suave rojo
  border:  "#F7C7CC",   // borde suave
  text:    "#7E121B"    // rojo texto
};

const getLevelInfo = (scoreRaw) => {
  const s = Number.isFinite(scoreRaw) ? scoreRaw : 0;

  if (s == 110) {
    return { key:"master", title:"Nivel Master",
      desc:"Dominan el caso clínico a la perfección.",
      tone:"success" };
  }
  if (s >= 90) {
    return { key:"experto", title:"Nivel Experto",
      desc:"Dominan el caso clínico, resolvieron casi todo con precisión.",
      tone:"success" };
  }
  if (s >= 70 && s <= 89) {
    return { key:"muy-buen-desempeno", title:"Muy buen desempeño",
      desc:"Demuestran buen criterio clínico, con algunos detalles por mejorar.",
      tone:"good" };
  }
  if (s >= 40 && s <= 69) {
    return { key:"competente", title:"Competente",
      desc:"Lograron avanzar, aunque con varios errores; aún hay áreas por reforzar.",
      tone:"neutral" };
  }
  if (s >= 10 && s <= 39) {
    return { key:"en-proceso", title:"En proceso",
      desc:"Resolvieron lo básico, pero requieren mayor análisis clínico.",
      tone:"warn" };
  }
  if (s >= 0 && s <= 9) {
    return { key:"minimo", title:"Mínimo alcanzado",
      desc:"Apenas superaron el reto, es necesario repasar los fundamentos.",
      tone:"alert" };
  }
  return { key:"retry", title:"Debes intentarlo de nuevo",
    desc:"El desempeño no fue suficiente, se recomienda repetir la experiencia.",
    tone:"retry" };
};

function Results({ intl }) {
  const lang = useLang();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [group, setCurrentGroup] = useState(
    useSelector((state) => state.group)
  );
  const [img, setImg] = useState(null);
  const [time, setTime] = useState(group?.time || 0);
  const [groupName, setGroupName] = useState(null);

  const level = getLevelInfo(group?.score ?? 0);

  /** Solo staff: resetea tablet como si no hubiera registro; la caché de medios del navegador se conserva. */
  const handleStealthResetSession = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearTabletLocalSession();
    dispatch(setGroup(null));
    dispatch(setGame(null));
    navigate(ROUTES.HOME, { replace: true });
  };

  useEffect(() => {
    if (group) {
      getGroupById(group?._id)
        .then((res) => {
          if (res.status === 200) {
            const fresh = res.data;

            // 1) calcula tiempos
            const reduxTime  = Number(group?.time)  || 0;       // lo que traías en Redux (más actualizado)
            const serverTime = Number(fresh?.time) || 0;        // lo que devuelve el backend
            const bestTime   = Math.max(reduxTime, serverTime); // nunca bajar

            // 2) estado local con datos frescos (y tiempo no decreciente)
            setCurrentGroup({ ...fresh, time: bestTime });
            setGroupName(fresh?.fullName || null);
            setImg(fresh?.image ? `${SERVER_URL}/${fresh.image}` : null);
            setTime((prev) => Math.max(prev, serverTime)); // evita el flicker hacia abajo

            // 3) sincroniza Redux y tus stats con el MISMO objeto
            const merged = {
              ...fresh,
              time: bestTime,
              lastVisited: ROUTES.RESULTS,
              finished: true,
            };
            dispatch(setGroup(merged));
            updateStats(merged);
          }
        })
        .catch((error) => {});
    }
  }, []);

  return (
    <div className="h-100 bg-notas">
      <div className="container-fluid h-100 d-flex flex-column align-items-center justify-content-start pt-5">
        
        {/* Título */}
        <div className="row text-center my-4">
          <h1 className="txt42 txtRed bold">
            {`Resultado del equipo ${group?.tableNum || "X"}`}
          </h1>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 d-flex align-items-center justify-content-between rounded shadow p-3" style={{ backgroundColor: "#ffffffaa"}}>
           <div className="d-flex flex-column align-items-center position-relative me-5">
              <img src={profileContainer} alt="" style={{ width: "350px" }} className="filterRed" />
              <div className="teamfoto mb-3">
                {img && <img src={img} alt="Equipo" style={{ width: "320px", height: "320px", borderRadius: "50%" }} />}
              </div>
              <div className="txt28 txtRed bold mt-3">
                {groupName || "Nombre equipo"}
              </div>
            </div>
            <div className="d-flex flex-column align-items-center">
              {/* Tiempo */}
              <div className="mb-4 text-center">
                <div className="txt28 txtRed bold mb-2">Tiempo de juego</div>
                <div className="d-flex align-items-center justify-content-center">
                  <div className="timer-big bold">
                    {Math.floor(time / 60).toString().padStart(2, "0")}
                  </div>
                  <div className="title1 px-2 bold txt-black txt32">:</div>
                  <div className="timer-big bold">
                    {(time % 60).toString().padStart(2, "0")}
                  </div>
                </div>
              </div>

              {/* Puntos */}
              <div className="text-center">
                <div className="txt28 txtRed bold mb-2">Puntos obtenidos</div>
                <div
                  className="px-5 py-3 rounded shadow-sm"
                  style={{
                    backgroundColor: "#b1000d",
                    borderRadius: "12px",
                    minWidth: "160px",
                  }}
                >
                  <div className="txt42 bold" style={{ color: "white" }}>
                    {group?.score ?? 0}
                  </div>
                </div>
              </div>
              <div className="mt-4 w-100 d-flex justify-content-center">
                <div className="w-100 d-flex justify-content-center">
                  <div
                    className="rounded shadow-sm"
                    style={{
                      background: "#fff",
                      border: `1px solid ${THEME.border}`,
                      borderLeft: `6px solid ${THEME.primary}`,
                      maxWidth: 720, width: "100%", padding: 16
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        {/* Etiqueta del nivel: pill rojo */}
                        <span className="txt20" style={{ color: THEME.text, fontWeight: 700 }}>
                          Nivel alcanzado
                        </span>
                        <span
                          style={{
                            backgroundColor: THEME.primary, color: "#fff",
                            borderRadius: 999, padding: "8px 14px",
                            fontWeight: 700
                          }}
                          className="txt20"
                        >
                          {level.title}
                        </span>
                        {/* Título secundario, misma jerarquía que tus labels */}
                      </div>
                    </div>

                    <p className="mb-0 txt16" style={{ color: "#333" }}>
                      {level.desc}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-100 d-flex legalGebro position-absolute px-5 justify-content-between align-items-end mb-2">
        <div className="d-flex flex-column align-items-end position-relative">
          {/* Zona táctil discreta (esquina del logo): no borra caché de vídeo/imagen, solo sesión local + Redux */}
          <button
            type="button"
            onClick={handleStealthResetSession}
            title=""
            aria-label="Reiniciar sesión del dispositivo"
            className="p-0 border-0 bg-transparent position-absolute"
            style={{
              bottom: 0,
              left: 0,
              width: 48,
              height: 48,
              opacity: 0.03,
              cursor: "default",
              zIndex: 2,
            }}
          />
          <img src={oncommit} width={75} alt="" />
        </div>
        <div className="d-flex justify-content-end">
          <img src={logoJohnsonAndJohnson} width={150} alt="Nutricia" />
        </div>
      </div>
    </div>
  );
}

export default injectIntl(Results);
